/**
 * WeeklyRecapCard — living weekly intelligence hub.
 *
 * Friday cron generates a review ("how did the week land?").
 * Sunday cron generates a prep ("what's queued for the week ahead?").
 * Both auto-email to the user.
 *
 * The prep items are interactive checkboxes — check them off and they
 * animate away. Once all are done the checklist collapses with a
 * congratulatory message. Checked state persists server-side.
 *
 * The metric deltas show live week-over-week comparisons that update
 * as synced data flows in throughout the week.
 */
import { useState, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { api } from '@/lib/api';
import { DataCard, StatusBadge, SkeletonList, QueryError } from '../ui';
import { AudioPlayButton } from '../AudioPlayButton';
import type { SourceCoverage } from '@waypoint/shared';
import { sees } from '@/lib/coverage';
import { NotConnected } from '../ui/NotConnected';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface MetricDelta {
  metricId: string;
  label: string;
  current: number;
  previous: number;
  deltaPct: number;
  direction: 'up' | 'down' | 'flat';
  isBetter: boolean;
}

interface Recap {
  id: string;
  kind: 'friday_review' | 'sunday_prep';
  week_start_date: string;
  generated_at: string;
  metric_deltas: MetricDelta[];
  highlights: string[];
  headline: string | null;
  narrative: string | null;
  next_week_plan: string[];
  completed_items: number[];
}

interface RecapResponse {
  recap: Recap | null;
  /** What the API could see when it served the stored recap (it withholds claims about sources it cannot). */
  coverage?: SourceCoverage;
  hint?: string;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function fmtMetric(m: MetricDelta): string {
  const isCurrency = m.metricId === 'rev' || m.metricId === 'pipe';
  if (!isCurrency) return String(m.current);
  if (m.current >= 1_000_000) return '$' + (m.current / 1_000_000).toFixed(1) + 'M';
  if (m.current >= 1_000) return '$' + Math.round(m.current / 1_000) + 'k';
  return '$' + m.current;
}

function deltaColorClass(m: MetricDelta): string {
  if (m.direction === 'flat') return 'text-ink-dim';
  const goodWay = (m.isBetter && m.direction === 'up') || (!m.isBetter && m.direction === 'down');
  return goodWay ? 'text-success' : 'text-danger';
}

function recapAge(generatedAt: string): string {
  const diff = Date.now() - new Date(generatedAt).getTime();
  const hrs = Math.floor(diff / 3_600_000);
  if (hrs < 1) return 'just now';
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

/* ------------------------------------------------------------------ */
/*  Prep checklist item                                                */
/* ------------------------------------------------------------------ */

function PrepItem({
  text,
  index,
  checked,
  onToggle,
  animatingOut,
}: {
  text: string;
  index: number;
  checked: boolean;
  onToggle: (index: number, checked: boolean) => void;
  animatingOut: boolean;
}) {
  return (
    <li
      className={clsx(
        'flex gap-2 items-start transition-all duration-300',
        animatingOut && 'opacity-0 max-h-0 -mt-1 overflow-hidden',
        !animatingOut && 'max-h-20',
      )}
    >
      <button
        type="button"
        onClick={() => onToggle(index, !checked)}
        className={clsx(
          'shrink-0 mt-0.5 w-4 h-4 rounded-sm border transition-all duration-200',
          checked
            ? 'bg-emerald-500 border-emerald-500 text-white'
            : 'border-border/60 hover:border-primary/40 bg-transparent',
        )}
      >
        {checked && (
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
            <polyline points="3.5 8 6.5 11 12.5 5" />
          </svg>
        )}
      </button>
      <span
        className={clsx(
          'text-xs leading-snug transition-all duration-200',
          checked ? 'text-ink-muted line-through' : 'text-ink',
        )}
      >
        {text}
      </span>
    </li>
  );
}

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

export function WeeklyRecapCard() {
  const queryClient = useQueryClient();
  const [animatingOut, setAnimatingOut] = useState<Set<number>>(new Set());
  const [showNarrative, setShowNarrative] = useState(false);

  const q = useQuery({
    queryKey: ['weekly-recap-latest'],
    queryFn: () => api<RecapResponse>('/v1/weekly-recap/latest'),
    staleTime: 5 * 60_000,
  });

  const recap = q.data?.recap;
  const isReview = recap?.kind === 'friday_review';
  const isPrep = recap?.kind === 'sunday_prep';

  const completedSet = useMemo(
    () => new Set(recap?.completed_items ?? []),
    [recap?.completed_items],
  );

  const pendingItems = useMemo(() => {
    if (!recap?.next_week_plan) return [];
    return recap.next_week_plan
      .map((text, i) => ({ text, index: i }))
      .filter((item) => !completedSet.has(item.index) && !animatingOut.has(item.index));
  }, [recap?.next_week_plan, completedSet, animatingOut]);

  const allDone =
    recap?.next_week_plan &&
    recap.next_week_plan.length > 0 &&
    pendingItems.length === 0 &&
    animatingOut.size === 0;

  const totalItems = recap?.next_week_plan?.length ?? 0;
  const doneCount = completedSet.size;
  const progressPct = totalItems > 0 ? Math.round((doneCount / totalItems) * 100) : 0;

  const toggleItem = useCallback(
    async (index: number, checked: boolean) => {
      if (!recap) return;

      if (checked) {
        setAnimatingOut((prev) => new Set(prev).add(index));
        setTimeout(() => {
          setAnimatingOut((prev) => {
            const next = new Set(prev);
            next.delete(index);
            return next;
          });
          queryClient.setQueryData<RecapResponse>(['weekly-recap-latest'], (old) => {
            if (!old?.recap) return old;
            return {
              ...old,
              recap: {
                ...old.recap,
                completed_items: [...old.recap.completed_items, index],
              },
            };
          });
        }, 350);
      } else {
        queryClient.setQueryData<RecapResponse>(['weekly-recap-latest'], (old) => {
          if (!old?.recap) return old;
          return {
            ...old,
            recap: {
              ...old.recap,
              completed_items: old.recap.completed_items.filter((i) => i !== index),
            },
          };
        });
      }

      try {
        await api(`/v1/weekly-recap/${recap.id}/check-item`, {
          method: 'PATCH',
          body: JSON.stringify({ index, checked }),
        });
      } catch {
        // Revert on failure
        queryClient.invalidateQueries({ queryKey: ['weekly-recap-latest'] });
      }
    },
    [recap, queryClient],
  );

  /* ---- Subtitle logic ---- */
  let subtitle = 'Auto-delivered Fri 4pm + Sun 6pm ET';
  if (recap) {
    const label = isReview ? 'Friday review' : 'Sunday prep';
    const date = new Date(recap.generated_at).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    });
    subtitle = `${label} · ${date} · ${recapAge(recap.generated_at)}`;
  }

  return (
    <DataCard
      title={isPrep ? 'Week Ahead' : 'Weekly Recap'}
      subtitle={subtitle}
      badge={
        totalItems > 0 && !allDone ? (
          <span className="flex items-center gap-1.5 rounded-full bg-primary/15 px-2 py-0.5 text-3xs font-semibold text-primary tabular-nums">
            {doneCount}/{totalItems} done
          </span>
        ) : undefined
      }
      headerRight={
        recap ? (
          <AudioPlayButton
            endpoint={`/v1/audio/recap/${recap.id}`}
            label="Listen"
          />
        ) : undefined
      }
    >
      {q.isLoading ? (
        <SkeletonList rows={4} />
      ) : q.isError ? (
        <QueryError error={q.error} onRetry={() => q.refetch()} compact />
      ) : null}

      {!q.isLoading && !recap && (
        <div className="text-2xs text-ink-dim py-2">
          <p>
            {q.data?.hint ??
              'Your first recap will be emailed automatically on Friday at 4pm ET.'}
          </p>
          <p className="mt-1.5 text-ink-dim/70">
            Sunday prep follows at 6pm ET. Both land in your inbox.
          </p>
        </div>
      )}

      {recap && (
        <div className="space-y-3">
          {/* Headline */}
          {recap.headline && (
            <div className="text-sm font-medium text-ink leading-snug">
              {recap.headline}
            </div>
          )}

          {/* The stored recap's figures are only shown for sources Waypoint can see now. */}
          {(['crm', 'money'] as const).filter((n) => !sees(q.data?.coverage, n)).map((n) => <NotConnected key={n} need={n} />)}

          {/* Metric grid */}
          {recap.metric_deltas?.length > 0 && (
            <div className="grid grid-cols-2 gap-1.5">
              {recap.metric_deltas.slice(0, 4).map((m) => (
                <div
                  key={m.metricId}
                  className="rounded-lg bg-surface-1 px-2.5 py-2"
                >
                  <div className="text-2xs uppercase tracking-wider text-ink-dim">
                    {m.label}
                  </div>
                  <div className="mt-0.5 flex items-baseline gap-1.5">
                    <div className="font-mono text-sm font-semibold tabular-nums text-ink">
                      {fmtMetric(m)}
                    </div>
                    <div
                      className={clsx(
                        'text-2xs font-semibold tabular-nums',
                        deltaColorClass(m),
                      )}
                    >
                      {m.direction === 'up'
                        ? '↑'
                        : m.direction === 'down'
                          ? '↓'
                          : '→'}{' '}
                      {Math.abs(m.deltaPct).toFixed(0)}%
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Narrative — collapsible for prep, always shown for review */}
          {recap.narrative && (
            <div>
              {isPrep ? (
                <button
                  type="button"
                  onClick={() => setShowNarrative((v) => !v)}
                  className="text-2xs font-medium text-primary hover:text-primary/80 transition-colors"
                >
                  {showNarrative ? 'Hide summary ▾' : 'Show summary ▸'}
                </button>
              ) : null}
              {(!isPrep || showNarrative) && (
                <div className="text-xs text-ink-muted leading-relaxed whitespace-pre-wrap mt-1">
                  {recap.narrative}
                </div>
              )}
            </div>
          )}

          {/* Prep checklist — interactive */}
          {recap.next_week_plan?.length > 0 && (
            <div className="space-y-2 border-t border-border/40 pt-2.5">
              {/* Header with progress bar */}
              <div className="flex items-center justify-between gap-2">
                <StatusBadge
                  status={allDone ? 'success' : 'neutral'}
                  label={
                    allDone
                      ? 'All done'
                      : isReview
                        ? 'Tee up for Monday'
                        : 'Monday priorities'
                  }
                  size="xs"
                />
                {totalItems > 1 && !allDone && (
                  <div className="flex items-center gap-1.5">
                    <div className="h-1 w-16 rounded-full bg-surface-3 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-emerald-500 transition-all duration-500"
                        style={{ width: `${progressPct}%` }}
                      />
                    </div>
                    <span className="text-3xs tabular-nums text-ink-muted">
                      {progressPct}%
                    </span>
                  </div>
                )}
              </div>

              {/* All-done state */}
              {allDone && (
                <div className="flex items-center gap-2 rounded-lg bg-emerald-500/10 px-3 py-2.5 transition-all duration-500">
                  <span className="text-emerald-500 text-base">&#x2705;</span>
                  <div>
                    <p className="text-xs font-medium text-emerald-700 dark:text-emerald-400">
                      Week is prepped.
                    </p>
                    <p className="text-2xs text-emerald-600/70 dark:text-emerald-400/60">
                      All {totalItems} priorities handled. Your next recap lands{' '}
                      {isPrep ? 'Friday 4pm' : 'Sunday 6pm'} ET.
                    </p>
                  </div>
                </div>
              )}

              {/* Active checklist */}
              {!allDone && (
                <ul className="space-y-2 list-none">
                  {recap.next_week_plan.map((text, i) => {
                    const isChecked = completedSet.has(i);
                    const isAnimating = animatingOut.has(i);
                    if (isChecked && !isAnimating) return null;
                    return (
                      <PrepItem
                        key={i}
                        text={text}
                        index={i}
                        checked={isChecked || isAnimating}
                        onToggle={toggleItem}
                        animatingOut={isAnimating}
                      />
                    );
                  })}
                </ul>
              )}
            </div>
          )}

          {/* Highlights tags */}
          {recap.highlights?.length > 0 && (
            <div className="flex flex-wrap gap-1 pt-1 border-t border-border/40">
              {recap.highlights.slice(0, 4).map((h, i) => (
                <span
                  key={i}
                  className="text-2xs px-1.5 py-0.5 rounded-sm bg-surface-1 text-ink-muted"
                >
                  {h}
                </span>
              ))}
            </div>
          )}

          <div className="text-2xs text-ink-dim font-mono pt-1">
            Week of {recap.week_start_date} · updated{' '}
            {new Date(recap.generated_at).toLocaleString()}
          </div>
        </div>
      )}
    </DataCard>
  );
}
